import React, { useState } from "react";
import { Sparkles, Clock, Clipboard, Loader2, Check, X, Calendar } from "lucide-react";
import { BASE_URL } from "../utils/constant";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui-custom/Dialog";

const AISummaryModal = ({ isOpen, onClose, notifications }) => {
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState("");
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");

    const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

    const generateSummary = async (date) => {
        setLoading(true);
        setError("");
        setSummary("");
        
        const targetDate = date || selectedDate;
        
        // Filter notifications by selected date
        const filteredNotifications = notifications.filter(n => {
            try {
                return new Date(n.date).toISOString().split('T')[0] === targetDate;
            } catch (e) {
                return false;
            }
        });

        if (filteredNotifications.length === 0) {
            setError(`No activities found for ${targetDate}.`);
            setLoading(false);
            return;
        }

        const accessToken = localStorage.getItem("access");
        try {
            const response = await fetch(`${BASE_URL}/core/chatbot/daily-summary/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    organiation: organizationId,
                    date: targetDate,
                    notifications: filteredNotifications
                }),
            });

            const data = await response.json();
            if (data.status) {
                setSummary(data.data.summary);
            } else {
                setError(data.message || "Failed to generate summary.");
            }
        } catch (err) {
            console.error("Error generating summary:", err);
            setError("An error occurred while connecting to SiteMaster AI.");
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatSummary = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, i) => (
            <p key={i} className="mb-2 last:mb-0">
                {line}
            </p>
        ));
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader className="border-b border-gray-100 bg-gradient-to-r from-[#3B0069] to-[#58267F] text-white p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 p-2 rounded-lg">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <DialogTitle className="text-lg font-bold text-white">
                                SiteMaster Smart Activity Summary
                            </DialogTitle>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </DialogHeader>

                <div className="p-6 overflow-y-auto max-h-[70vh]">
                    <div className="flex flex-col gap-6">
                        {/* Controls */}
                        <div className="flex flex-wrap items-center gap-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-semibold text-purple-700 uppercase tracking-wider mb-1">
                                    Select Date
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 bg-white border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-2 items-end">
                                <button
                                    onClick={() => {
                                        const today = new Date().toISOString().split('T')[0];
                                        setSelectedDate(today);
                                        generateSummary(today);
                                    }}
                                    className="px-4 py-2 bg-white text-[#58267F] border border-purple-200 rounded-lg text-sm font-semibold hover:bg-purple-100 transition-colors flex items-center gap-2"
                                >
                                    Summarize Today
                                </button>
                                <button
                                    onClick={() => generateSummary()}
                                    disabled={loading || !selectedDate}
                                    className="px-6 py-2 bg-[#58267F] text-white rounded-lg text-sm font-semibold hover:bg-[#4a1f6b] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    {loading ? (
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Generating...</span>
                                        </div>
                                    ) : (
                                        <span>Show Summary</span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Result Area */}
                        <div className="min-h-[200px] bg-gray-50 rounded-2xl border border-gray-100 relative group">
                            {loading ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                                    <div className="relative mb-4">
                                        <div className="w-12 h-12 rounded-full border-4 border-purple-100 border-t-purple-600 animate-spin" />
                                        <Sparkles className="absolute inset-0 m-auto w-4 h-4 text-purple-600 animate-pulse" />
                                    </div>
                                    <p className="text-gray-600 font-medium">SiteMaster AI is analyzing activities...</p>
                                    <p className="text-xs text-gray-400 mt-1 italic">This may take a few seconds</p>
                                </div>
                            ) : error ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                                    <div className="bg-red-50 p-4 rounded-full mb-3">
                                        <Clock className="w-6 h-6 text-red-400" />
                                    </div>
                                    <p className="text-red-600 font-medium">{error}</p>
                                </div>
                            ) : summary ? (
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                            <Sparkles className="w-4 h-4 text-purple-600" />
                                            Summary for {selectedDate}
                                        </h4>
                                        <button
                                            onClick={handleCopy}
                                            className="p-2 hover:bg-purple-100 rounded-lg transition-colors text-purple-600 flex items-center gap-1.5 text-xs font-semibold"
                                        >
                                            {copied ? (
                                                <><Check className="w-3.5 h-3.5" /> Copied!</>
                                            ) : (
                                                <><Clipboard className="w-3.5 h-3.5" /> Copy Summary</>
                                            )}
                                        </button>
                                    </div>
                                    <div className="text-sm text-gray-700 leading-relaxed bg-white border border-gray-100 p-4 rounded-xl shadow-inner whitespace-pre-wrap">
                                        {formatSummary(summary)}
                                    </div>
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                                    <div className="bg-purple-50 p-4 rounded-full mb-3">
                                        <Sparkles className="w-6 h-6 text-purple-300" />
                                    </div>
                                    <p className="text-gray-500 font-medium">Select a date and click "Show Summary" to begin.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t border-gray-100 flex justify-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold flex items-center gap-2">
                        Powered by SiteMaster AI Logic
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AISummaryModal;
